// 页面配置: how this browser lays the console out and colours it.
//
// Per browser, in localStorage — a preference of whoever is sitting at it,
// not a platform setting: everything the navbar's 自定义设置 drawer
// changes — the theme (浅色, 深色, 双色), layout, sounds and 全屏. The theme
// colour is the platform's (系统配置, carried by the branding) and is
// applied here too.
import { computed, reactive, ref, watch, type Ref } from 'vue'
import { layoutDefaults, skin as brandSkin } from '@/utils/branding'
import { isNarrow } from '@/utils/responsive'
import { palette, parseHex } from '@/utils/palette'

/** Arco's named palettes, as the theme colour was first stored, by their base colour. */
const NAMED_SKINS: Record<string, string> = {
  arcoblue: '#165DFF',
  blue: '#3491FA',
  cyan: '#14C9C9',
  green: '#00B42A',
  lime: '#9FDB1D',
  gold: '#F7BA1E',
  orange: '#FF7D00',
  orangered: '#F77234',
  red: '#F53F3F',
  magenta: '#F5319D',
  pinkpurple: '#D91AD9',
  purple: '#722ED1',
}
/** A stored theme colour as #RRGGBB: a palette's name or the colour itself. */
export function skinHex(skin: string | undefined): string {
  if (skin && NAMED_SKINS[skin]) return NAMED_SKINS[skin]
  return skin && parseHex(skin) ? skin.toUpperCase() : NAMED_SKINS.arcoblue
}

export interface LayoutPrefs {
  /** The top bar. Off, a floating button keeps 页面配置 reachable. */
  navbar: boolean
  /** The bar's height (on a wide screen; a narrow one keeps 60); null is
      the platform's default (系统配置). */
  navbarHeight: number | null
  /** The side menu (on a wide screen; a narrow one always uses the drawer). */
  menu: boolean
  /** Its width; null is the platform's default. */
  menuWidth: number | null
  /** The heading above each page's content. */
  pageTitle: boolean
  /** 色弱模式: colours inverted, as Arco Pro does it. */
  colorWeak: boolean
  /** 灰色模式: everything in greys. */
  grayscale: boolean
  /** 新消息提示音: a chime when a reply comes in. */
  sound: boolean
  /** 语音播报: the words spoken after it. */
  speech: boolean
}

export const DEFAULT_PREFS: Readonly<LayoutPrefs> = {
  navbar: true,
  navbarHeight: null,
  menu: true,
  menuWidth: null,
  pageTitle: true,
  colorWeak: false,
  grayscale: false,
  sound: true,
  speech: false,
}
export const MENU_WIDTH_MIN = 180
export const MENU_WIDTH_MAX = 320
export const NAVBAR_HEIGHT_MIN = 48
export const NAVBAR_HEIGHT_MAX = 72
// The bar's height on a narrow screen, whatever the setting.
const NARROW_NAVBAR = 60

const KEY = 'cdnx_user_layout'

// What a stored copy is, so a changed default can reach those who never
// chose: version 1 stored every field, the menu's old 220 among them;
// version 2 the bar's 60 and the menu's 210, the defaults then, which are
// the platform's now.
const VERSION = 3

function load(): Partial<LayoutPrefs> {
  try {
    const raw = localStorage.getItem(KEY)
    const v = raw ? (JSON.parse(raw) as Partial<LayoutPrefs> & { v?: number; skin?: string }) : {}
    if ((v.v ?? 1) < 2 && v.menuWidth === 220) delete v.menuWidth
    if ((v.v ?? 1) < 3) {
      if (v.navbarHeight === 60) delete v.navbarHeight
      if (v.menuWidth === 210) delete v.menuWidth
    }
    // The theme colour is the platform's now.
    delete v.skin
    delete v.v
    return v
  } catch {
    return {}
  }
}

export const prefs = reactive<LayoutPrefs>({ ...DEFAULT_PREFS, ...load() })

/** The bar's height as chosen: the browser's, else the platform's. */
export const chosenNavbarHeight = computed({
  get: () => prefs.navbarHeight ?? layoutDefaults.value.navbarHeight,
  set: (v: number) => (prefs.navbarHeight = v),
})
/** …and as drawn: that on a wide screen. */
export const navbarHeight = computed(() => (isNarrow.value ? NARROW_NAVBAR : chosenNavbarHeight.value))
/** The side menu's width: the browser's, else the platform's. */
export const menuWidth = computed({
  get: () => prefs.menuWidth ?? layoutDefaults.value.menuWidth,
  set: (v: number) => (prefs.menuWidth = v),
})

/**
 * Whether the 页面配置 drawer is open. One drawer, opened from the navbar
 * or — with the navbar switched off — from a floating button; it lives
 * outside the navbar so switching the navbar off does not close it.
 */
export const settingsOpen = ref(false)

/**
 * Which of the navbar's panels is out (NavAccount's, NavBilling's), so
 * one opening puts the other away at once instead of both hanging, one
 * over the other, until the first's leave delay runs out.
 */
export const navPanel = ref<string | null>(null)

/**
 * 全屏, from 页面配置. Followed rather than assumed: Esc leaves full
 * screen without asking.
 */
export const fullscreen = ref(false)
document.addEventListener('fullscreenchange', () => (fullscreen.value = !!document.fullscreenElement))
export function setFullscreen(on: boolean) {
  if (on) document.documentElement.requestFullscreen?.().catch(() => {})
  else if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {})
}

export function resetPrefs() {
  Object.assign(prefs, DEFAULT_PREFS)
  theme.value = 'light'
}

/**
 * The theme: 浅色, 深色 — Arco's dark tokens hang off body[arco-theme] —
 * or 双色, the bar and menu dark over light pages (styles/twotone.css,
 * .tone-dark where twoTone says). Kept apart from the layout under the
 * key the light/dark switch always used, so the choice carries over.
 */
export const THEMES = ['light', 'dark', 'twotone'] as const
export type Theme = (typeof THEMES)[number]
const THEME_KEY = 'cdnx_user_theme'
function loadTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return THEMES.includes(v as Theme) ? (v as Theme) : 'light'
  } catch {
    return 'light'
  }
}
export const theme = ref<Theme>(loadTheme())
export const twoTone = computed(() => theme.value === 'twotone')
watch(
  theme,
  (v) => {
    if (v === 'dark') document.body.setAttribute('arco-theme', 'dark')
    else document.body.removeAttribute('arco-theme')
    try {
      localStorage.setItem(THEME_KEY, v)
    } catch {
      /* private mode: the choice just isn't remembered */
    }
  },
  { immediate: true },
)

// Arco colours everything "primary" from --primary-1…10, which it sets on
// body to the arcoblue ramp. Setting them to the theme colour's own shades
// (utils/palette.ts, Arco's algorithm) recolours the whole console — any
// colour, not only Arco's named ones — with the dark ramp under 深色模式
// and in 双色模式's dark zone, where a variable built from another would
// otherwise keep the light values (it is worked out where it is declared).
// Links get the same, or they would stay blue. One stylesheet, after
// Arco's, a step more specific than its own body rule.
function applySkin(skin: string) {
  const hex = skinHex(skin)
  const decl = (ramp: string[]) => ramp.map((c, i) => `--primary-${i + 1}: ${c}; --link-${i + 1}: ${c};`).join(' ')
  const light = decl(palette(hex))
  const dark = decl(palette(hex, true))
  let el = document.getElementById('skin-zone')
  if (!el) {
    el = document.createElement('style')
    el.id = 'skin-zone'
    document.head.appendChild(el)
  }
  el.textContent = `html body { ${light} } html body[arco-theme='dark'], .tone-dark { ${dark} }`
}

// On the root element: a filter anywhere else would become the containing
// block of every fixed-position element under it — dialogs, drawers.
function applyFilters(p: LayoutPrefs) {
  const f = [p.colorWeak && 'invert(80%)', p.grayscale && 'grayscale(100%)'].filter(Boolean).join(' ')
  document.documentElement.style.filter = f
}

watch(
  prefs,
  (p) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...p, v: VERSION }))
    } catch {
      /* private mode: the choice just isn't remembered */
    }
    applyFilters(p)
  },
  { deep: true, immediate: true },
)
watch(brandSkin, applySkin, { immediate: true })
// The bar's height for every rule that follows it (App.vue's bar and the
// phone drawer's head, teleported out of its reach).
watch(navbarHeight, (h) => document.documentElement.style.setProperty('--navbar-h', `${h}px`), { immediate: true })

/**
 * A navbar button's tooltip that keeps quiet while the button's own menu
 * is open. The tooltip opens below the button — there is no room above
 * the navbar — right where the menu opens, and the menu's first entry sat
 * under it. Arco's `disabled` does not close a tooltip already showing,
 * so the tooltip is driven instead:
 *
 *   const { visible: tipVisible, change: onTip } = quietTooltip(open)
 *   <a-tooltip :popup-visible="tipVisible" @popup-visible-change="onTip">
 */
export function quietTooltip(open: Ref<boolean>) {
  const hovered = ref(false)
  // Opening or closing the menu forgets the hover: the pointer has gone
  // into the menu, and the tooltip would otherwise come back by itself.
  watch(open, () => (hovered.value = false))
  return {
    visible: computed(() => hovered.value && !open.value),
    change: (v: boolean) => (hovered.value = v),
  }
}
